import {
  IMAGE_LOAD_FILTERS,
  NON_EDITABLE_PRODUCT_VENDORS,
  STOREFRONT_ACCESS_TOKEN,
  STOREFRONT_API_URL,
  SelectedVariantAvailability,
} from "../constants";
import { getFullyQualifiedUrl, formatSnakeCaseToCamelCase } from "./general";
import { translateShippingNote } from "./translation";
import variantMetadataQuery from "../components/Product/graphql/variant-metadata.graphql";
import tolstoyVariantMetadataQuery from "../components/Product/graphql/tolstoy-variant-metadata.graphql";
import {
  type LanguageCode,
  type CountryCode,
  type Maybe,
  type MediaImage,
  type MetafieldReference,
  type Video,
} from "@shopify/hydrogen-react/storefront-api-types";
import { parseGid } from "@shopify/hydrogen-react";
import { type PartialDeep } from "type-fest";

import {
  type SelectedVariant,
  type ImageMedia,
  type VideoMedia,
  type ProductState,
  type CartProduct,
  type CartProductVariant,
  type CartProductConnection,
  type CartProductVariantConnection,
  type CartMediaImage,
  type CartGiftWithPurchaseConnection,
  type CartProductBasedGiftWithPurchase,
  type CartMerchandise,
  type SelectedOption,
  type OPProductConnection,
  type OPProduct,
  type OPProductVariant,
  type OPProductVariantConnection,
  type CartMediaVideo,
  type SizeGuideDataConnection,
  type StandardizedSizeGuideData,
  type CustomMetaobjectValueFieldType,
} from "@tablescape/state";
// eslint-disable-next-line no-restricted-imports
import { handleize } from "@react/utilities/general";

export const updatePictureElement = ({
  alt,
  element,
  source,
}: {
  alt?: string;
  element: HTMLPictureElement;
  source: ImageMedia["image"];
}) => {
  const newImageUrl = getFullyQualifiedUrl(source.url);
  const newImageVersion = newImageUrl.searchParams.get("v") ?? "";

  const currentImage = element.querySelector<HTMLImageElement>("img");

  if (currentImage === null) {
    return;
  }

  const currentImageUrl = getFullyQualifiedUrl(
    currentImage.getAttribute("src") ?? ""
  );

  const currentSourceElements = element.querySelectorAll("source");
  const currentSources = Array.from(currentSourceElements).map(
    (currentSourceElement) =>
      currentSourceElement
        ?.getAttribute("srcset")
        ?.split(/,\s?/)
        .map((sourceFragment) => {
          const [sourceUrl, sourceSize] = sourceFragment.split(/\s+/);
          return {
            url: getFullyQualifiedUrl(sourceUrl),
            size: sourceSize,
          };
        })
        .filter(Boolean)
  );

  if (currentImageUrl.searchParams.has("v")) {
    currentImageUrl.searchParams.set("v", newImageVersion);
  }
  const updatedImageUrl = new URL(
    `${newImageUrl.origin}${newImageUrl.pathname}${currentImageUrl.search}`
  );

  const updateImage = () => {
    if (alt !== undefined) {
      currentImage.setAttribute("alt", alt);
    }
    currentImage.setAttribute("src", updatedImageUrl.toString());
    element.classList.remove(...IMAGE_LOAD_FILTERS);

    currentImage.removeEventListener("load", updateImage);
  };

  currentImage.addEventListener("load", updateImage);

  currentSourceElements.forEach((currentSourceElement, sourceIdx) => {
    const sourceArray = currentSources[sourceIdx];
    if (!sourceArray) return;

    const updatedSources = sourceArray.map(({ size, url }) => {
      if (url.searchParams.has("v")) {
        url.searchParams.set("v", newImageVersion);
      }
      const updatedSourceUrl = new URL(
        `${newImageUrl.origin}${newImageUrl.pathname}${url.search}`
      );

      return `${updatedSourceUrl.toString()} ${size}`;
    });

    currentSourceElement.setAttribute("srcset", updatedSources.join(", "));
  });
};

export const updateVideoElement = ({
  element,
  sources,
}: {
  element: HTMLVideoElement;
  sources: VideoMedia["sources"];
}) => {
  const currentSourceElements =
    element.querySelectorAll<HTMLSourceElement>("source");

  currentSourceElements.forEach((currentSourceElement) => {
    const matchingSource = sources.find(
      (source: { mimeType: string; url: string }) =>
        source.mimeType === currentSourceElement.getAttribute("type")
    );

    if (
      matchingSource &&
      element.canPlayType(matchingSource.mimeType) &&
      element.currentSrc !== matchingSource.url
    ) {
      element.setAttribute("src", matchingSource.url);
    }
  });

  element.classList.remove(...IMAGE_LOAD_FILTERS);
};

export const formatVariant = (activeVariant: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}): SelectedVariant => ({
  ...activeVariant,
  alternateImage: activeVariant.alternateImage?.reference ?? null,
  availableForSale: activeVariant.availableForSale,
  badgeImage: activeVariant.badgeImage?.reference ?? null,
  badgeSale: activeVariant.badgeSale?.value ?? null,
  badgeText: activeVariant.badgeText?.value ?? null,
  badgeType: activeVariant.badgeType?.value ?? null,
  compareAtPrice: activeVariant.compareAtPrice
    ? activeVariant.compareAtPrice?.amount * 100
    : null,
  disableWaitlist: activeVariant.disableWaitlist,
  giftWithPurchase: (() => {
    const base = formatGiftWithPurchase(activeVariant.giftWithPurchase);
    if (!base) return null;
    const fullVariants = deriveAutoAddVariantsFromProducts(
      base.autoAddProducts,
      activeVariant.selectedOptions
    );
    const autoAddVariants =
      fullVariants.length > 0
        ? fullVariants.map((v) => ({
            id: String(v.id).replace("gid://shopify/ProductVariant/", ""),
            shippingNote: v.shippingNote ?? undefined,
          }))
        : null;
    return {
      ...base,
      autoAddVariants: autoAddVariants ?? undefined,
      thumbnailUrl: base.thumbnailUrl?.replace(/^https?:/, "") || null,
    };
  })(),
  handle: activeVariant.handle,
  id: activeVariant.id.replace("gid://shopify/ProductVariant/", ""),
  kittedProduct: activeVariant.kittedProduct,
  mainProduct: activeVariant.mainProduct,
  media: activeVariant.media?.references.nodes.map(
    (node: ImageMedia | VideoMedia) => {
      switch (node.contentType) {
        case "IMAGE": {
          return {
            ...node,
            image: {
              ...node.image,
              url: node.image?.url.replace(/^https?:/, ""),
            },
          };
        }
        case "VIDEO": {
          return {
            ...node,
            source: node.sources.map(
              (source: { mimeType: string; url: string }) => ({
                ...source,
                url: source.url.replace(/^https?:/, ""),
              })
            ),
          };
        }
      }

      return node;
    }
  ),
  name: activeVariant.title,
  optionCategory: activeVariant.optionCategory?.value ?? null,
  price: Math.round(activeVariant.price.amount * 100),
  productId: activeVariant.productId.replace("gid://shopify/Product/", ""),
  productTitle: activeVariant.productTitle,
  selectedOptions: activeVariant.selectedOptions,
  shippingNote:
    translateShippingNote(activeVariant.shippingNote?.value) ?? null,
  supplementalProduct: activeVariant.supplementalProduct,
  title: activeVariant.title,
  variantBundleAvailableForSale: (() => {
    const componentNodes =
      activeVariant.variantBundleComponents?.references?.nodes;
    if (componentNodes && componentNodes.length > 0) {
      return !componentNodes.some(
        (node: { availableForSale: boolean }) => !node.availableForSale
      );
    }
    return activeVariant.availableForSale;
  })(),
  variantBundleComponentImages:
    activeVariant.variantBundleComponents?.references?.nodes?.map(
      (node: { featuredImage?: { url: string } }) => ({
        contentType: "IMAGE",
        image: {
          url: node.featuredImage?.url.replace(/^https?:/, ""),
        },
      })
    ) ?? [],
});

export const updateMaestroVariants = (
  node: HTMLElement,
  activeVariant: Record<string, unknown> | HTMLElement
) => {
  if (activeVariant instanceof HTMLElement) {
    Object.keys(activeVariant.dataset)
      .filter((k) => k.startsWith("mtrProductVariant"))
      .forEach((k) => {
        node.dataset[k] = activeVariant.dataset[k];
      });
  } else {
    Object.keys(node.dataset)
      .filter((k) => k.startsWith("mtrProductVariant"))
      .map((k) => ({
        attr: k,
        key: k.replace("mtrProductVariant", "").toLowerCase(),
      }))
      .forEach(({ attr, key }) => {
        const value = activeVariant[key];
        node.dataset[attr] =
          typeof value === "string" ? value : String(value ?? "");
      });
  }
};

export const fetchVariantMetadata = async (
  {
    country,
    language,
    productHandle,
    selectedOptions,
  }: {
    country: string;
    language: string;
    productHandle: string;
    selectedOptions: SelectedOption[];
  },
  { abortController }: { abortController: AbortController }
) => {
  if (abortController.signal.aborted) {
    abortController = new AbortController();
  }

  try {
    const response = await fetch(STOREFRONT_API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": STOREFRONT_ACCESS_TOKEN,
      } as HeadersInit,
      signal: abortController.signal,
      body: JSON.stringify({
        query: variantMetadataQuery,
        variables: { productHandle, selectedOptions, country, language },
      }),
    });

    if (!response.ok) {
      return Promise.resolve({
        error: true,
        status: response.status,
        statusText: response.statusText,
      });
    }

    return response.json();
  } catch (e) {
    console.error(e);
  }
};

export const fetchTolstoyVariantMetadata = async (
  {
    country,
    id,
    language,
  }: {
    country: string;
    id: string;
    language: string;
  },
  { abortController }: { abortController: AbortController }
) => {
  if (abortController.signal.aborted) {
    abortController = new AbortController();
  }

  try {
    const response = await fetch(STOREFRONT_API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": STOREFRONT_ACCESS_TOKEN,
      } as HeadersInit,
      signal: abortController.signal,
      body: JSON.stringify({
        query: tolstoyVariantMetadataQuery,
        variables: { id, country, language },
      }),
    });

    if (!response.ok) {
      return Promise.resolve({
        error: true,
        status: response.status,
        statusText: response.statusText,
      });
    }

    return response.json();
  } catch (e) {
    console.error(e);
  }
};

const VARIANT_CACHE = new Map<string, SelectedVariant>();

export const getVariantMetadata = async (
  {
    country,
    language,
    productHandle,
    selectedOptions,
    variantId,
  }: {
    country: CountryCode;
    language: LanguageCode;
    productHandle: string;
    selectedOptions: SelectedOption[];
    variantId?: string;
  },
  {
    abortController,
    loader,
  }: { abortController: AbortController; loader?: () => void }
) => {
  if (variantId && VARIANT_CACHE.has(variantId)) {
    return VARIANT_CACHE.get(variantId);
  } else {
    if (loader) {
      loader();
    }

    const response = await fetchVariantMetadata(
      {
        productHandle,
        selectedOptions,
        country,
        language,
      },
      { abortController }
    );

    if (response.error) {
      return;
    }

    const {
      data: { product },
    } = response;

    if (!product || !product.handle || !product.variant) {
      return;
    }

    const { handle, id, title, variant: variantMetadata } = product;
    const selectedVariant = formatVariant({
      handle,
      ...variantMetadata,
      productId: id,
      productTitle: title,
      country,
    });
    VARIANT_CACHE.set(
      variantMetadata.id.replace("gid://shopify/ProductVariant/", ""),
      selectedVariant
    );
    return selectedVariant;
  }
};

const TOLSTOY_VARIANT_CACHE = new Map<string, SelectedVariant>();

export const getTolstoyVariantMetadata = async (
  {
    country,
    id,
    language,
  }: {
    country: CountryCode;
    id?: string;
    language: LanguageCode;
  },
  {
    abortController,
    loader,
  }: { abortController: AbortController; loader?: () => void }
) => {
  if (id && VARIANT_CACHE.has(id)) {
    return VARIANT_CACHE.get(id);
  } else {
    if (loader) {
      loader();
    }

    if (!id) {
      return;
    }

    const response = await fetchTolstoyVariantMetadata(
      {
        id,
        country,
        language,
      },
      { abortController }
    );

    if (response.error) {
      return;
    }

    const {
      data: { node: productVariant },
    } = response;

    if (!productVariant) {
      return;
    }

    TOLSTOY_VARIANT_CACHE.set(id, productVariant);
    return productVariant;
  }
};

export const getProductHandleFromUrl = (url: string) => {
  const productHandle = url.split("/products/")[1];
  return productHandle;
};

export const getSelectedVariantsPrice = (
  selectedVariants: SelectedVariant[],
  maxPrice?: number
) => {
  const combinedPrice = selectedVariants.reduce(
    (total, variant) => total + variant.price,
    0
  );

  if (maxPrice) {
    return Math.min(combinedPrice, maxPrice);
  }

  return combinedPrice;
};

export const getSelectedVariantsCompareAtPrice = (
  selectedVariants: SelectedVariant[],
  discountType?: string
) => {
  const discountDetected = selectedVariants.some(
    (variant) => variant.compareAtPrice
  );

  if (!discountDetected && discountType !== "bundle") {
    return null;
  }

  return selectedVariants.reduce((total, variant) => {
    if (variant.compareAtPrice) {
      return total + variant.compareAtPrice;
    }

    return total + variant.price;
  }, 0);
};

export const findSelectedVariants = (products: {
  [key: string]: ProductState;
}) =>
  Object.values(products)
    .filter((product) => !!product)
    .map(({ selectedVariant }) => selectedVariant);

export const formatMerchandise = (
  merchandise: PartialDeep<
    CartMerchandise,
    {
      recurseIntoArrays: true;
    }
  >
): PartialDeep<
  CartMerchandise,
  {
    recurseIntoArrays: true;
  }
> => ({
  ...merchandise,
  shippingNote: merchandise.shippingNote?.value
    ? {
        ...merchandise.shippingNote,
        value:
          translateShippingNote(merchandise.shippingNote.value) ??
          merchandise.shippingNote.value,
      }
    : merchandise.shippingNote,
});

export const isMediaImage = (
  media: PartialDeep<
    MetafieldReference,
    {
      recurseIntoArrays: true;
    }
  >
): media is MediaImage =>
  "mediaContentType" in media && media?.mediaContentType === "IMAGE";

export const isMediaVideo = (
  media: PartialDeep<
    MetafieldReference,
    {
      recurseIntoArrays: true;
    }
  >
): media is Video =>
  "mediaContentType" in media && media?.mediaContentType === "VIDEO";

export const formatMsrpValue = (value: string | number | undefined = 0) =>
  String((typeof value === "string" ? parseInt(value) : value) * 100);

export const getMsrpForVariant = (variant: Partial<CartProductVariant>) => {
  return formatMsrpValue(
    variant.compareAtPrice?.amount || variant.price?.amount
  );
};

export const formatGiftWithPurchase = (
  giftWithPurchase: CartGiftWithPurchaseConnection | null | undefined
): CartProductBasedGiftWithPurchase | null => {
  if (!giftWithPurchase || !giftWithPurchase?.reference) {
    return null;
  }

  const { reference } = giftWithPurchase;

  return {
    ...reference,
    autoAddProducts:
      reference?.autoAddProducts?.references?.nodes
        ?.filter((node): node is CartProductConnection => !!node)
        .map((node) => formatProduct(node)) ?? null,
    bannerText: reference?.bannerText?.value ?? null,
    description: reference?.description?.value ?? null,
    giftBadgeText: reference?.giftBadgeText?.value ?? null,
    eligibleLoyaltyTiers: reference?.eligibleLoyaltyTiers?.value
      ? JSON.parse(reference.eligibleLoyaltyTiers.value)
      : null,
    products:
      reference?.products?.references?.nodes
        ?.filter((node): node is CartProductConnection => !!node)
        .map((node) => formatProduct(node)) ?? null,
    thumbnailUrl: reference?.thumbnail?.reference?.image?.url ?? null,
    title: reference?.title?.value ?? null,
  };
};

/**
 * Filters variants to the eligible gift subset.
 * Returns undefined when the metafield is absent, empty, all IDs are stale,
 * or the filtered set equals the full set (no actual restriction),
 * so downstream `eligibleGiftVariants ?? variants` falls back correctly.
 */
export const filterEligibleGiftVariants = (
  variants: CartProductVariant[],
  eligibleIds: string[] | null
): CartProductVariant[] | undefined => {
  if (!eligibleIds?.length) return undefined;
  const idSet = new Set(eligibleIds);
  const filtered = variants.filter((v) => idSet.has(v.id));
  if (filtered.length === 0 || filtered.length === variants.length)
    return undefined;
  return filtered;
};

/** Pick one variant per GWP product (color match or first available). Shared by V1 formatVariant and V2 AddToCartButton. */
export const deriveAutoAddVariantsFromProducts = (
  products: CartProduct[] | null | undefined,
  selectedOptions: Array<Partial<SelectedOption> | null | undefined> | undefined
): CartProductVariant[] => {
  if (!products?.length || selectedOptions == null) return [];
  const normalizedSelectedOptions = selectedOptions.filter(
    (option): option is Partial<SelectedOption> => option != null
  );
  const isOptionNameColor = (option: Partial<SelectedOption>) =>
    option.name === "Color" || option.name === "Colour";
  const activeVariantColor =
    normalizedSelectedOptions.filter(isOptionNameColor)[0]?.value;
  const result: CartProductVariant[] = [];
  for (const product of products) {
    const giftVariants = product.eligibleGiftVariants ?? product.variants;
    if (!product.availableForSale || !giftVariants.length) continue;
    const matchingColorVariant = giftVariants.find(
      (v) =>
        v.selectedOptions?.filter(isOptionNameColor)[0]?.value ===
        activeVariantColor
    );
    const autoAddVariant = matchingColorVariant?.availableForSale
      ? matchingColorVariant
      : giftVariants.find((v) => v.availableForSale);
    if (autoAddVariant) result.push(autoAddVariant);
  }
  return result;
};

const safeJsonParse = (value: string | null): string[] | null => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

export const formatProductVariant = (
  variant: CartProductVariantConnection | OPProductVariantConnection
): CartProductVariant | OPProductVariant => {
  const bundleNodes = variant.variantFixedBundleComponents?.references?.nodes;
  const isVirtualLegacyBundle = Boolean(bundleNodes?.length && !variant.sku);

  return {
    ...variant,
    alternateImage: variant.alternateImage?.reference ?? null,
    availableForSale: isVirtualLegacyBundle
      ? !bundleNodes!.some((node) => !node.availableForSale)
      : variant.availableForSale,
    badgeImage: variant.badgeImage?.reference ?? null,
    badgeText: variant.badgeText?.value ?? null,
    badgeType: variant.badgeType?.value ?? null,
    collectionNames: safeJsonParse(variant.collectionNames?.value ?? null),
    colorGroup: safeJsonParse(variant.colorGroup?.value ?? null),
    compareAtPriceDisplay: variant.compareAtPriceDisplay?.value ?? null,
    disableWaitlist: variant.disableWaitlist?.value === "true",
    variantFixedBundleComponents:
      variant.variantFixedBundleComponents?.references.nodes?.map((node) => {
        return {
          ...node,
          shippingNote: translateShippingNote(node.shippingNote?.value),
        };
      }) ?? null,
    giftWithPurchase: formatGiftWithPurchase(variant.giftWithPurchase),
    hidden: variant.hidden?.value === "true",
    hoverImage: variant.hoverImage?.reference ?? null,
    id: variant.id,
    imageUrl:
      variant.media?.references?.nodes.find(
        (
          node: PartialDeep<
            MetafieldReference,
            {
              recurseIntoArrays: true;
            }
          >
        ): node is ImageMedia => isMediaImage(node)
      )?.image?.url ?? null,
    media:
      variant.media?.references?.nodes.filter(
        (
          node
        ): node is Extract<
          MetafieldReference,
          CartMediaImage | CartMediaVideo
        > => node && (isMediaImage(node) || isMediaVideo(node))
      ) ?? [],
    optionCategory: variant.optionCategory?.value ?? null,
    optionLabel: variant.optionLabel?.value ?? null,
    priceBadgeText: variant.priceBadgeText?.value ?? null,
    title: variant.title,
    selectedOptions: variant.selectedOptions,
    shippingNote: translateShippingNote(variant.shippingNote?.value),
    sku: variant.sku,
  };
};

const normalizeMetafieldString = (
  value: string | null | undefined
): string | null => value || null;

export type BrowseProductDisplayFields = {
  displayTitle?: Maybe<{ value: string }>;
  productSize?: Maybe<{ value: string }>;
};

export const getBrowseProductDisplay = (
  product: BrowseProductDisplayFields
) => ({
  displayTitle: normalizeMetafieldString(product.displayTitle?.value),
  productSize: normalizeMetafieldString(product.productSize?.value),
});

export const getBrowseTitle = (
  product: BrowseProductDisplayFields & { title: string }
) => getBrowseProductDisplay(product).displayTitle ?? product.title;

export const formatProduct = (
  product: CartProductConnection | OPProductConnection
): CartProduct | OPProduct => {
  const { bundle, ...rest } = product;

  const variants = product.variants.nodes.map((node) =>
    formatProductVariant(node)
  );
  const eligibleGiftVariantIds =
    product.eligibleGiftVariants?.references?.nodes
      ?.filter((node): node is { id: string } => Boolean(node?.id))
      .map((node) => node.id) ?? null;
  const eligibleGiftVariants = filterEligibleGiftVariants(
    variants,
    eligibleGiftVariantIds
  );

  return {
    ...rest,
    availableForSale: product.availableForSale,
    crossProductBridge: product.crossProductBridge
      ? {
          label: product.crossProductBridge.reference.label.value,
          softLoad:
            product.crossProductBridge.reference.softLoad?.value === "true",
          options: product.crossProductBridge.reference.options.references.nodes
            .filter((node) => node.product?.reference)
            .map((node) => {
              if (!node.product?.reference) {
                throw new Error("Product reference should exist after filter");
              }
              return {
                label: node.label.value,
                product: {
                  handle: node.product.reference.handle,
                  id: node.product.reference.id,
                  onlineStoreUrl: node.product.reference.onlineStoreUrl,
                },
              };
            }),
        }
      : null,
    labels: product.labels?.value ? JSON.parse(product.labels.value) : null,
    productFixedBundleComponents:
      product.bundle?.reference.productFixedBundleComponents?.references?.nodes?.map(
        (node) => ({
          featuredImage: node.featuredImage,
          handle: "handle" in node ? node.handle ?? "" : "",
          id: node.id,
          title: "title" in node ? node.title ?? "" : "",
        })
      ) ?? null,
    productSelectorId: product.productSelectorId?.value ?? null,
    description: product.description?.value ?? null,
    eligibleGiftVariants,
    handle: product.handle,
    id: product.id,
    onlineStoreUrl: product.onlineStoreUrl ?? null,
    optionCategories: product.optionCategories?.value
      ? JSON.parse(product.optionCategories.value)
      : null,
    productType: product.productType,
    setSize: product.setSize?.value ?? null,
    title: product.title,
    variants,
  };
};

export const formatProductSizeGuideData = (
  sizeGuideData: SizeGuideDataConnection
): StandardizedSizeGuideData[] => {
  return sizeGuideData.sizeGuide.references.nodes.map((node) => {
    return {
      customDataFields: node.customDataFields?.value
        ? (() => {
            try {
              const parsed = JSON.parse(node.customDataFields.value);
              return Array.isArray(parsed) &&
                parsed.every(
                  (item): item is CustomMetaobjectValueFieldType =>
                    typeof item === "string" &&
                    ["text", "image", "number"].includes(item)
                )
                ? parsed
                : [];
            } catch {
              return [];
            }
          })()
        : [],
      customDataImage: node.customDataImage?.reference?.image?.url ?? undefined,
      customDataLabel: node.customDataLabel?.value ?? undefined,
      dimensionsDataFields: node.dimensionsDataFields?.value
        ? (() => {
            try {
              const parsed = JSON.parse(node.dimensionsDataFields.value);
              return Array.isArray(parsed)
                ? parsed.map((dimensionField: unknown) =>
                    typeof dimensionField === "string"
                      ? formatSnakeCaseToCamelCase(dimensionField)
                      : String(dimensionField)
                  )
                : [];
            } catch {
              return [];
            }
          })()
        : [],
      fields: (() => {
        const rawFields = convertMetaobjectFieldsToObject(node.fields);
        const cleanFields: Record<string, string | string[]> = {};
        Object.entries(rawFields).forEach(([key, value]) => {
          if (value !== undefined) {
            cleanFields[key] =
              typeof value === "number" ? String(value) : value;
          }
        });
        return cleanFields;
      })(),
      name: node.name?.value ?? undefined,
      type: node.type.value,
    };
  });
};

export const isProductVariantAvailableForSale = (
  variant: CartProductVariant
) => {
  return (
    !variant.hidden &&
    variant.availableForSale &&
    (variant.shippingNote
      ? handleize(variant.shippingNote) !== SelectedVariantAvailability.SOLD_OUT
      : true)
  );
};

export const selectedOrFirstPurchasableVariant = (
  product: CartProduct,
  selectedVariantId?: CartProductVariant["id"]
) =>
  product.variants.find(
    (variant) =>
      variant.id === selectedVariantId &&
      isProductVariantAvailableForSale(variant)
  ) ?? product.variants.find(isProductVariantAvailableForSale);

export const isGiftCard = (product: CartProduct | OPProduct): boolean => {
  const productType = product.productType?.toLowerCase() ?? "";

  return productType.includes("gift card");
};

export const getIntelligemsPriceByVariantId = (
  variantId?: CartProductVariant["id"]
) => {
  const igPriceByVariantId = window?.igData?.price?.getPriceByVariantId(
    parseGid(variantId).id
  );

  return igPriceByVariantId ? parseFloat(igPriceByVariantId) : null;
};

export const getIntelligemsComparePriceByVariantId = (
  variantId?: CartProductVariant["id"]
) => {
  const igPriceByVariantId = window?.igData?.price?.getComparePriceByVariantId(
    parseGid(variantId).id
  );

  return igPriceByVariantId ? parseFloat(igPriceByVariantId) : null;
};

export const mapIntelligemsPriceToVariant = (variant: CartProductVariant) => {
  const intelligemsPrice = getIntelligemsPriceByVariantId(variant.id)?.toFixed(
    1
  );

  if (intelligemsPrice && intelligemsPrice !== variant.price.amount) {
    return {
      ...variant,
      price: {
        ...variant.price,
        amount: intelligemsPrice || variant.price.amount,
      },
    };
  }
  return variant;
};

export const COMBO_OPTION_URL_PARAM = "combo_option";

const METAOBJECT_GID_PATTERN = /^gid:\/\/shopify\/Metaobject\/(\d+)$/;

export const getNumericMetaobjectId = (id: string | number) => {
  const idString = id.toString();
  const metaobjectGidMatch = idString.match(METAOBJECT_GID_PATTERN);

  if (metaobjectGidMatch) {
    return metaobjectGidMatch[1];
  }

  return idString;
};

export const resolveComboOptionIndex = (
  comboOptionIds: string[],
  comboOptionId: string | null
) => {
  if (!comboOptionId || comboOptionIds.length === 0) {
    return null;
  }

  const normalizedComboOptionId = getNumericMetaobjectId(comboOptionId);
  const matchedIndex = comboOptionIds.findIndex(
    (id) => getNumericMetaobjectId(id) === normalizedComboOptionId
  );

  return matchedIndex >= 0 ? matchedIndex : null;
};

export const resolveInitialComboIndexFromUrl = (
  comboOptionIds: string[],
  defaultSelectedIndex: number
) => {
  if (typeof window === "undefined" || comboOptionIds.length === 0) {
    return defaultSelectedIndex;
  }

  const searchParams = new URLSearchParams(window.location.search);
  const comboOptionId = searchParams.get(COMBO_OPTION_URL_PARAM);
  const resolvedIndex = resolveComboOptionIndex(comboOptionIds, comboOptionId);

  return resolvedIndex ?? defaultSelectedIndex;
};

export const updateUrlVariantId = (id?: CartProductVariant["id"]) => {
  if (typeof window === "undefined") {
    return;
  }

  const url = new URL(window.location.href);
  url.searchParams.set("variant", parseGid(id).id);
  history.replaceState(null, "", url);
};

// Updates combo_option only; leaves variant intact so ?combo_option=…&variant=…
// deep-links still pre-select the main-PDP product variant (isCurrentPdpProduct tab).
export const updateUrlComboOption = (metaobjectId?: string | number) => {
  if (typeof window === "undefined" || metaobjectId === undefined) {
    return;
  }

  const url = new URL(window.location.href);
  url.searchParams.set(
    COMBO_OPTION_URL_PARAM,
    getNumericMetaobjectId(metaobjectId)
  );

  const nextUrl = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState(null, "", nextUrl);
};

export const convertMetaobjectFieldsToObject = (
  metaobjectFields: {
    key: string;
    type: string;
    value: string;
  }[]
): Partial<Record<string, string | number | string[]>> => {
  const formattedObject: Partial<Record<string, string | number | string[]>> =
    {};
  metaobjectFields.forEach((metaobjectField) => {
    const key = formatSnakeCaseToCamelCase(metaobjectField.key);
    if (
      metaobjectField.type.includes("list") &&
      JSON.parse(metaobjectField.value)
    ) {
      formattedObject[key] = JSON.parse(metaobjectField.value).map(
        (el: string) => formatSnakeCaseToCamelCase(el)
      );
    } else if (metaobjectField.type.includes("number")) {
      formattedObject[key] = Number(metaobjectField.value);
    } else {
      formattedObject[key] = metaobjectField.value;
    }
  });
  return formattedObject;
};

export const checkIfNonEditableProductVendor = (productVendor: string) => {
  return NON_EDITABLE_PRODUCT_VENDORS.includes(productVendor);
};
